/**
 * Worldly wire protocol (spec section 8.4).
 *
 * One WebSocket carries everything in-match. JSON, discriminated on `t`.
 * REST handles only out-of-match concerns.
 *
 * INVARIANT 5 — the client never receives the answer key for an UNANSWERED
 * question, in any derivable form. Nothing in this file may carry an answer
 * before the corresponding rung has been RESOLVED server-side.
 *
 * Revealing it after the rung is resolved is not merely allowed, it is now
 * required (change 3): a wrong answer busts the round, so there is nothing
 * left to protect, and throwing away the educational payload at the exact
 * moment it would land hardest is the worst thing this protocol could do.
 * `AnswerReveal` is that reveal, and the type system is what keeps it on the
 * right side of the line — see the long comment above `sealReveal`.
 *
 * INVARIANT (opp.state) — `OppStateMessage.lngLat` must be `undefined` unless
 * the RECEIVING player has already locked their own rung-1 pinpoint for that
 * round. Populating it earlier leaks the answer.
 */

import { isPace } from './tiers';
import type { Pace, ProbeType, Tier } from './tiers';
import { isMatchPhase } from './scoring';
import { WINDOW_RTT_CREDIT_CAP_MS } from './constants';
import type {
  Decision,
  MatchPhase,
  PinpointAttemptPolicy,
  PinpointAttemptState,
  RoundOutcome,
} from './scoring';

/**
 * Bumped 1 -> 2 for the playtest round: the answer submission changed shape
 * (`optionIdx` -> a tagged `answer`), `rung.result` gained a `status`
 * discriminant and a structured `reveal`, and the round payload's rungs became
 * a discriminated union on answer kind. None of that is forwards compatible,
 * and `hello` is version-gated, so a stale client fails cleanly at the
 * handshake instead of mid-round.
 */
export const PROTOCOL_VERSION = 3;

/** [longitude, latitude] — MapLibre order, everywhere, always. */
export type LngLat = [number, number];

/**
 * `group` is a free-for-all of up to `GROUP_MAX_SEATS`. It differs from `quick`
 * only in how the table is assembled — every rule inside the room is the same
 * one (invariant 6), which is why this is a kind and not a mode.
 */
export type MatchKind = 'quick' | 'friend' | 'daily' | 'group';

/**
 * The most seats a lobby can hold, and what every friend link is minted for.
 *
 * SHARED, because the client picks the capacity and the server enforces it,
 * and a client asking for more seats than the box allows is a link that fails
 * at the moment somebody tries to use it. The gateway can still be configured
 * lower (`GROUP_MAX_SEATS`) and clamps what it is asked for; this is the
 * number both ends agree on when nobody has said otherwise.
 */
export const GROUP_MAX_SEATS = 6;

/**
 * The three game modes, in the order they are offered.
 *
 * An ARRAY as well as a union, the way `TIERS` is, so a screen that offers a
 * choice between them can map over one list instead of typing all three out
 * and quietly falling one behind when a fourth arrives.
 */
export const GAME_MODES = ['country', 'city', 'state'] as const;
export type GameMode = (typeof GAME_MODES)[number];

/** Where a seat's input comes from. Invariant 1 makes these indistinguishable. */
export type OpponentSource = 'live' | 'ghost';

export interface OpponentInfo {
  readonly playerId: string;
  readonly handle: string;
  readonly rating: number;
  /** Never surfaced in the UI — the fallback must be seamless (spec section 4). */
  readonly source: OpponentSource;
}

export interface PlayerScore {
  readonly playerId: string;
  readonly handle: string;
  readonly total: number;
  readonly roundsBanked: number;
  readonly roundsBusted: number;
  readonly biggestPot: number;
}

export interface RatingDelta {
  readonly tier: Tier;
  readonly before: number;
  readonly after: number;
  readonly rdBefore: number;
  readonly rdAfter: number;
}

export interface RoundSideSummary {
  readonly playerId: string;
  readonly finalRung: number;
  readonly banked: boolean;
  readonly pot: number;
  readonly outcome: RoundOutcome;
  readonly total: number;
  /** Safe here: the round is over. */
  readonly pinLngLat: LngLat | null;
  readonly targetCca3: string;
  /** How many pinpoint attempts this seat spent. 0 when it never tapped. */
  readonly pinpointAttempts?: number;
}

// ---------------------------------------------------------------------------
// Answer kinds — change 5 and change 6
// ---------------------------------------------------------------------------

/**
 * How a rung is ANSWERED. This is the discriminant the whole question surface
 * hangs off: `RoundPayloadRung`, `AnswerValue` and `RevealBody` all agree on
 * it, so a rung, the submission for it, and the reveal after it can never
 * disagree about what kind of question was asked.
 *
 * Modelled as a union rather than as optional fields on one shape because the
 * shapes genuinely differ — 2 options, 4 options, an ordering, or NO options
 * at all — and "options?: Option[] | undefined" cannot express which of those
 * a given rung is. A discriminant can, and it makes the client's renderer a
 * total switch rather than a chain of presence checks.
 */
export const ANSWER_KINDS = ['choice', 'boolean', 'ordering', 'numeric'] as const;
export type AnswerKind = (typeof ANSWER_KINDS)[number];

export function isAnswerKind(v: unknown): v is AnswerKind {
  return typeof v === 'string' && (ANSWER_KINDS as readonly string[]).includes(v);
}

/**
 * A rung's shape as it appears in the round payload. Two entries beyond the
 * answer kinds: `pinpoint` (rung 1, answered on the globe) and `sealed` (a
 * deeper rung whose prompt is still ciphertext — spec 8.2).
 */
export type RungKind = AnswerKind | 'pinpoint' | 'sealed';

/** Canonical option ids for a true/false rung, so option ORDER can vary. */
export const TRUE_OPTION_ID = 'tf:true';
export const FALSE_OPTION_ID = 'tf:false';

// ---------------------------------------------------------------------------
// Open numeric answers (change 6)
// ---------------------------------------------------------------------------

/**
 * Quantities the open-numeric rung may ask for. NUMBERS ONLY — never a place
 * name.
 *
 * `un-year` is the flagship and the reason the list exists. "Year of
 * independence" is genuinely ambiguous for a large fraction of the world —
 * independence from whom, and which of several dates; Germany, China and the
 * UK itself have no defensible single answer — whereas UN ACCESSION is a
 * single authoritative unambiguous date covering ~193 states. Spec section 6's
 * contested-content policy rules the first out and lets the second in.
 */
export const NUMERIC_QUANTITIES = [
  'un-year',
  'land-borders',
  'population-millions',
  'area-rank',
  'city-latitude',
  'city-distance-km',
] as const;
export type NumericQuantity = (typeof NUMERIC_QUANTITIES)[number];

export function isNumericQuantity(v: unknown): v is NumericQuantity {
  return typeof v === 'string' && (NUMERIC_QUANTITIES as readonly string[]).includes(v);
}

/**
 * How close counts as close. A RULE, never a number derived from the answer:
 * "within 10% of the truth" says nothing about the truth, whereas shipping the
 * computed window would spell out the answer's magnitude (invariant 5).
 */
export type NumericTolerance =
  | { readonly kind: 'exact' }
  | { readonly kind: 'absolute'; readonly delta: number }
  | { readonly kind: 'relative'; readonly fraction: number };

/**
 * Everything the client needs to render a numeric input, and nothing that
 * narrows the answer.
 *
 * !! `min`, `max`, `step` AND `tolerance` ARE CONSTANTS OF THE QUANTITY. !!
 * They are the same for every country and every match. Deriving any of them
 * from the answer — a range "around" the truth, a tolerance scaled to it —
 * turns this struct into an oracle and breaks invariant 5. `NUMERIC_SPECS`
 * below is the only sanctioned source, and `@geogame/rules` asserts that a
 * generated numeric rung's spec is reference-equal to its entry.
 */
export interface NumericAnswerSpec {
  readonly quantity: NumericQuantity;
  /** Inclusive lower bound of the input control. */
  readonly min: number;
  /** Inclusive upper bound of the input control. */
  readonly max: number;
  /** Granularity of the control. 1 everywhere so far. */
  readonly step: number;
  /** Unit shown beside the wheel/keypad, e.g. "year", "borders". */
  readonly unit: string;
  /** One-line hint under the prompt, e.g. "a year between 1945 and 2011". */
  readonly hint: string;
  /** Digits a wheel or keypad should offer. Derived from `max`, not from the answer. */
  readonly digits: number;
  readonly tolerance: NumericTolerance;
}

/**
 * The four numeric quantities, fully specified.
 *
 * ===========================================================================
 * PARTIAL CREDIT: DECIDED, NOT SILENTLY PICKED.
 * ===========================================================================
 *
 * The rule is: EXACT pays the golden multiplier; NEAR pays the rung's NORMAL
 * (non-golden) multiplier; anything else busts. A near miss is therefore worth
 * exactly what a normal round's terminal rung is worth — no new number is
 * invented, and "close" has an obvious meaning to a player.
 *
 * Whether a NEAR band exists at all is per quantity, and the deciding question
 * is always the same: DOES THE BAND HAND A BLIND GUESSER A FLOOR?
 *
 *   un-year        -> EXACT ONLY. UN accession years cluster brutally: 51
 *                     states joined in 1945, 16 in 1955, 17 in 1960, and a
 *                     further cluster across 1991-1993. A +/-5 band centred on
 *                     1960 covers 1955-1965 and would hit a double-digit
 *                     percentage of the pool with zero geography, which
 *                     destroys the 0% guess floor that is the entire reason
 *                     change 6 exists. Exact or nothing.
 *   land-borders   -> EXACT ONLY. The range is 0..14 and most of the mass sits
 *                     on 2..5; any band is most of the distribution.
 *   population     -> +/-10%, relative. The quantity is genuinely continuous,
 *                     the range spans three orders of magnitude, and 10% of
 *                     the truth is narrow everywhere on it — a blind guesser
 *                     picking uniformly over 0..1500 hits a 10% window around
 *                     a typical answer well under 1% of the time.
 *   area-rank      -> +/-5 absolute out of ~195 ranks: 11/195 = 5.6% blind,
 *                     and the near band pays a third of the exact band, so the
 *                     blind EV contribution is ~1.9% of the golden pot.
 *
 * `numericCreditBand` is the isomorphic grader; the server is authoritative
 * and the client uses it only for optimistic UI (invariant 6).
 */
export const NUMERIC_SPECS: Readonly<Record<NumericQuantity, NumericAnswerSpec>> = {
  'un-year': {
    quantity: 'un-year',
    // 1945 is the founding; 2011 is South Sudan, the newest member state.
    min: 1945,
    max: 2011,
    step: 1,
    unit: 'year',
    hint: 'A year between 1945 and 2011.',
    digits: 4,
    tolerance: { kind: 'exact' },
  },
  'land-borders': {
    quantity: 'land-borders',
    min: 0,
    // China and Russia both border 14 sovereign, non-disputed states.
    max: 14,
    step: 1,
    unit: 'borders',
    hint: 'How many countries share a land border with it.',
    digits: 2,
    tolerance: { kind: 'exact' },
  },
  'population-millions': {
    quantity: 'population-millions',
    min: 0,
    // India and China are ~1.4bn; the ceiling leaves headroom without inviting
    // a wheel nobody can spin.
    max: 1500,
    step: 1,
    unit: 'million people',
    hint: 'Population to the nearest million.',
    digits: 4,
    tolerance: { kind: 'relative', fraction: 0.1 },
  },
  'area-rank': {
    quantity: 'area-rank',
    min: 1,
    max: 200,
    step: 1,
    unit: 'rank by area',
    hint: '1 is the largest country by land area.',
    digits: 3,
    tolerance: { kind: 'absolute', delta: 5 },
  },
  /*
   * THE GOLDEN QUESTION FOR A CITY PIN.
   *
   * `city-latitude` asked how far a city sits from the equator — which the
   * player had just been shown, because rung 1 puts their pin on the globe.
   * Distance to ANOTHER city is the opposite: the globe helps, and that is the
   * point, but it does not answer. Estimating a great-circle distance from a
   * sphere you can see is a real skill and exactly the one this game teaches.
   *
   * Relative tolerance, not absolute: 25% of 300 km is a different question
   * from 25% of 15,000 km, and a fixed band would be trivial at one end and
   * impossible at the other.
   *
   * WHY 25 AND NOT 15. This band started at 15% on the guess-floor argument
   * that governs every other quantity here. That argument is sound and it was
   * applied to the wrong question. Every other numeric rung asks for something
   * a player can KNOW — an accession year, a border count, a rank — so a wide
   * band rewards not knowing it. Great-circle distance between two cities is
   * not knowable; it is ESTIMATED, off a sphere the player is looking at, and
   * the estimate is what the rung is actually testing. A band tight enough to
   * demand better than a fifth is not measuring the estimate, it is measuring
   * luck, and the rung reads as unanswerable.
   *
   * The cost is real and worth stating: a blind guesser picking uniformly over
   * 0..20,100 lands inside a +/-25% window around a typical 8,000 km answer
   * about a fifth of the time, against an eighth at 15%. That buys NEAR, which
   * pays the ordinary terminal multiplier — never the golden one — so the
   * floor it hands the golden pot is still zero. EXACT remains exact.
   */
  'city-distance-km': {
    quantity: 'city-distance-km',
    min: 0,
    // Two antipodal points are ~20,015 km apart; nothing on Earth exceeds it.
    max: 20_100,
    step: 1,
    unit: 'km',
    hint: 'Great-circle distance between the two cities.',
    digits: 5,
    tolerance: { kind: 'relative', fraction: 0.25 },
  },
  'city-latitude': {
    quantity: 'city-latitude',
    min: 0,
    max: 90,
    step: 1,
    unit: 'degrees latitude',
    hint: 'Distance from the equator, rounded to a whole degree.',
    digits: 2,
    tolerance: { kind: 'absolute', delta: 3 },
  },
};

/** How a numeric submission scored. `near` pays the NON-golden multiplier. */
export const NUMERIC_CREDIT_BANDS = ['exact', 'near', 'miss'] as const;
export type NumericCreditBand = (typeof NUMERIC_CREDIT_BANDS)[number];

/**
 * Grade a numeric submission. Isomorphic (invariant 6): the server runs it
 * authoritatively, the client runs the identical code for the reveal card.
 */
export function numericCreditBand(
  spec: NumericAnswerSpec,
  correctValue: number,
  submitted: number,
): NumericCreditBand {
  if (!Number.isFinite(submitted) || !Number.isFinite(correctValue)) return 'miss';
  if (submitted === correctValue) return 'exact';
  const delta = Math.abs(submitted - correctValue);
  switch (spec.tolerance.kind) {
    case 'exact':
      return 'miss';
    case 'absolute':
      return delta <= spec.tolerance.delta ? 'near' : 'miss';
    case 'relative':
      return delta <= Math.abs(correctValue) * spec.tolerance.fraction ? 'near' : 'miss';
    default:
      return 'miss';
  }
}

/**
 * What "close" is worth on this quantity, in one line, for the player.
 *
 * The near band existed from the start and nothing ever said so. A player
 * looking at "Great-circle distance, to the nearest kilometre" reasonably reads
 * it as exact-or-nothing, decides the rung is unanswerable, and either guesses
 * wildly or stops taking golden rungs — which is the band failing at the only
 * job it had. A tolerance the player cannot see does not soften the question,
 * it just makes the scoring look arbitrary when the reveal comes.
 *
 * Derived from `tolerance` rather than written into `hint` so the sentence
 * cannot drift from the grader. Returns null where there is no band, because
 * "exact or nothing" is what the bounds already imply and a line saying so
 * would be noise.
 */
export function toleranceHint(spec: NumericAnswerSpec): string | null {
  switch (spec.tolerance.kind) {
    case 'relative':
      return `Within ${Math.round(spec.tolerance.fraction * 100)}% still scores.`;
    case 'absolute':
      return `Within ${spec.tolerance.delta} ${spec.unit} still scores.`;
    case 'exact':
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Answer values — what a submission carries
// ---------------------------------------------------------------------------

/**
 * A submitted answer. An INDEX for anything with options (including an
 * ordering, whose permutation rides the index as a Lehmer rank — see
 * `encodeOrdering` in `@geogame/rules`), or a NUMERIC VALUE for the open rung.
 *
 * Tagged rather than "optionIdx?: number; value?: number", so a submission
 * cannot be both, cannot be neither, and the grader's switch is total.
 */
export type AnswerValue =
  | { readonly kind: 'index'; readonly optionIdx: number }
  | { readonly kind: 'numeric'; readonly value: number };

/** Upper bound on a submitted option index. 5-row ordering ranks top out at 119. */
export const MAX_OPTION_INDEX = 1_000;
/** Bounds on a submitted numeric value. Every quantity is a non-negative integer. */
export const MIN_NUMERIC_ANSWER = 0;
export const MAX_NUMERIC_ANSWER = 1_000_000_000;

export function indexAnswer(optionIdx: number): AnswerValue {
  return { kind: 'index', optionIdx };
}

export function numericAnswer(value: number): AnswerValue {
  return { kind: 'numeric', value };
}

/** The option index, or null when this submission was numeric. */
export function answerOptionIdx(a: AnswerValue): number | null {
  return a.kind === 'index' ? a.optionIdx : null;
}

/** The numeric value, or null when this submission was an index. */
export function answerNumericValue(a: AnswerValue): number | null {
  return a.kind === 'numeric' ? a.value : null;
}

export function isAnswerValue(v: unknown): v is AnswerValue {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const r = v as Record<string, unknown>;
  if (r['kind'] === 'index') {
    const i = r['optionIdx'];
    return typeof i === 'number' && Number.isInteger(i) && i >= 0 && i <= MAX_OPTION_INDEX;
  }
  if (r['kind'] === 'numeric') {
    const n = r['value'];
    return (
      typeof n === 'number' &&
      Number.isInteger(n) &&
      n >= MIN_NUMERIC_ANSWER &&
      n <= MAX_NUMERIC_ANSWER
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// C -> S
// ---------------------------------------------------------------------------

export interface HelloMessage {
  readonly t: 'hello';
  readonly v: number;
  readonly resumeToken?: string;
  /** Anonymous display handle; server may override. */
  readonly handle?: string;
}

export interface PingMessage {
  readonly t: 'ping';
  /** Client send instant, client clock (spec 8.1). */
  readonly t0: number;
}

export interface QueueMessage {
  readonly t: 'queue';
  readonly tier: Tier;
  readonly gameMode?: GameMode;
  readonly kind?: MatchKind;
  readonly courseId?: string;
  /** Friend-duel share token; single-attempt, server-consumed. */
  readonly shareToken?: string;
}

export interface DequeueMessage {
  readonly t: 'dequeue';
}

export interface PinpointMessage {
  readonly t: 'pinpoint';
  readonly roundIdx: number;
  /** ISO alpha-3 of the country tapped, or null when the tap hit ocean. */
  readonly entityCode: string | null;
  readonly lngLat: LngLat;
  /** Client clock instant of the tap (spec 8.3). */
  readonly clientTs: number;
  /**
   * Which attempt the client BELIEVES this is, 1-based (change 4). Advisory
   * only: the server counts attempts itself and grades against its own count.
   * Present so a duplicated frame can be recognised rather than silently
   * consuming an attempt the player did not spend.
   */
  readonly attempt?: number;
}

export interface PushMessage {
  readonly t: 'push';
  readonly roundIdx: number;
  readonly clientTs: number;
}

export interface BankMessage {
  readonly t: 'bank';
  readonly roundIdx: number;
  readonly clientTs: number;
}

export interface AnswerMessage {
  readonly t: 'answer';
  readonly roundIdx: number;
  readonly rung: number;
  /** An option index or a numeric value. See `AnswerValue`. */
  readonly answer: AnswerValue;
  readonly clientTs: number;
}

/**
 * Start the match sitting in this player's lobby, with whoever is in it.
 *
 * ANY member may send it. The roster is on everybody's screen, so "start when
 * we can both see each other" is a decision the group has already made
 * together — putting it behind a host would just mean waiting for one
 * particular person to look at their phone.
 */
export interface LobbyStartMessage {
  readonly t: 'lobby.start';
}

/**
 * Change the name this player is sitting under, while they are sitting there.
 *
 * A name used to be settable in exactly one place: the home screen, before a
 * match. Somebody who arrived on a shared link never saw that screen -- they
 * landed on the invitation, accepted, and appeared in the roster as
 * `PolarFjord417`, with nowhere to say otherwise. In a group of friends, a
 * table of anonymous handles is the one thing a lobby exists to prevent.
 *
 * NOT A NEW IDENTITY. `pid` never moves; only the label does. It is the same
 * rule `hello` already follows -- a token authenticates who you are, not what
 * you are called -- said again for a socket that is already open.
 */
export interface RenameMessage {
  readonly t: 'rename';
  readonly handle: string;
}

/**
 * Change what the lobby is going to play, without minting a new link.
 *
 * The settings came off the link, which was fine for one match and wrong for
 * an evening: a group that wanted to switch from Countries to Cities after a
 * few rounds had to send everyone home, change the settings, and redistribute
 * a fresh link to everybody. The lobby outlives the link that made it, so the
 * lobby is where the settings belong.
 *
 * ANY member may send it, for the same reason any member may send
 * HOST ONLY, and `lobby.start` deliberately is NOT.
 *
 * Both used to be open to anybody, on the reasoning that the roster and the
 * settings are on everybody's screen and putting either behind one person
 * means waiting for them to look at their phone. That argument is still right
 * about STARTING and was wrong about configuring: six people who can all
 * change the mode produce a screen that changes under whoever is reading it,
 * and the fix for that is not a race.
 *
 * So the settings have one owner, who can hand them on (`lobby.promote`), and
 * the start button stays where everybody can reach it.
 *
 * Every field is optional, and an omitted field is left alone.
 */
export interface LobbyConfigureMessage {
  readonly t: 'lobby.configure';
  readonly tier?: Tier;
  readonly gameMode?: GameMode;
  /**
   * Country mode only. Null clears it. A course in any other mode makes
   * `generateMatch` throw, so the gateway drops it rather than trusting this.
   */
  readonly courseId?: string | null;
  /** How long every round gets. See `Pace`: not a difficulty. */
  readonly pace?: Pace;
}

/**
 * Hand the settings to somebody else.
 *
 * Only the host may send it, and the named player must already be seated --
 * promoting an absent id would leave a lobby nobody can configure, which is
 * the exact failure the automatic transfer on disconnect exists to prevent.
 */
export interface LobbyPromoteMessage {
  readonly t: 'lobby.promote';
  readonly playerId: string;
}

export type ClientMessage =
  | HelloMessage
  | PingMessage
  | QueueMessage
  | LobbyStartMessage
  | LobbyConfigureMessage
  | LobbyPromoteMessage
  | RenameMessage
  | DequeueMessage
  | PinpointMessage
  | PushMessage
  | BankMessage
  | AnswerMessage;

export type ClientMessageType = ClientMessage['t'];

// ---------------------------------------------------------------------------
// The correct-answer reveal (change 3)
// ---------------------------------------------------------------------------

/**
 * What the reveal SAYS, per answer kind. The player's own pick travels
 * alongside the truth so the card can show them side by side — red beside
 * green — without the client having to remember what it sent.
 *
 * `rung` is carried inside the body, not just on the frame, so the two can be
 * cross-checked: `parseServerMessage` rejects a `rung.result` whose reveal
 * belongs to a different rung, which is the cheap runtime half of the
 * guarantee the type gives statically.
 */
export type RevealBody =
  | {
      readonly kind: 'pinpoint';
      readonly rung: number;
      /** The target. Safe: the pinpoint rung is out of attempts and resolved. */
      readonly correctCca3: string;
      /** Where to fly the globe. */
      readonly correctLngLat: LngLat;
      /** City name in city mode. Present only after the pinpoint resolves. */
      readonly correctLabel?: string;
      /** The player's last tap, or null if they never tapped. */
      readonly pickedCca3: string | null;
      readonly pickedLngLat: LngLat | null;
    }
  | {
      readonly kind: 'choice';
      readonly rung: number;
      readonly correctIndex: number;
      readonly pickedIndex: number | null;
      /**
       * What the options were CALLED, in option order.
       *
       * Only sent for a map choice, whose options reached the client as bare
       * coordinates — "you tapped the dot at -81.7, 41.5" teaches nobody
       * anything, and "that was Cleveland; Columbus is the capital" is the
       * whole point of the rung. Post-resolution, so it is a reveal field and
       * not a payload one (invariant 5, change 3).
       */
      readonly labels?: readonly string[];
    }
  | {
      readonly kind: 'boolean';
      readonly rung: number;
      /** Index into the rung's two options; their ids say which is which. */
      readonly correctIndex: number;
      readonly pickedIndex: number | null;
      /** Whether the STATEMENT was true. Lets the card say so in words. */
      readonly statementIsTrue: boolean;
      /**
       * The true version of the statement, in words.
       *
       * "False" on its own teaches nothing — the fact worth carrying away from
       * "Sydney is the capital of Australia" is that Canberra is. Post-
       * resolution only, same gate as `correctIndex`.
       */
      readonly correction?: string;
    }
  | {
      readonly kind: 'ordering';
      readonly rung: number;
      /** `correctOrder[k]` is the index into `options` belonging in position k. */
      readonly correctOrder: readonly number[];
      readonly pickedOrder: readonly number[] | null;
    }
  | {
      readonly kind: 'numeric';
      readonly rung: number;
      readonly quantity: NumericQuantity;
      readonly correctValue: number;
      readonly pickedValue: number | null;
      readonly band: NumericCreditBand;
    };

/**
 * Phantom brand. Type-only: it has no runtime representation, so it survives
 * `JSON.stringify` by simply not being there, and `parseServerMessage`
 * restores it on the way in.
 */
declare const RUNG_RESOLVED: unique symbol;

/**
 * A `RevealBody` that has been PROVEN to belong to a resolved rung.
 *
 * ===========================================================================
 * HOW THE TYPE CARRIES THE GUARANTEE (the brief asked; this is the answer).
 * ===========================================================================
 *
 * Three mechanisms, and the first two are compile-time:
 *
 * 1. NOMINAL: `AnswerReveal` carries a required property keyed by a
 *    module-private `unique symbol`. No object literal can satisfy it, in this
 *    package or any other, so `reveal: { kind: 'choice', correctIndex: 2 }`
 *    does not type-check ANYWHERE. The only value of this type in the whole
 *    program is one `sealReveal` returned.
 *
 * 2. PROOF OF FINALITY: `sealReveal` demands a `RungVerdict` whose `final`
 *    field is the LITERAL `true`, not `boolean`. A caller holding
 *    `attemptsRemaining > 0` cannot pass `final: someBoolean` — it has to
 *    narrow, which in practice means writing the `if` that separates "still in
 *    play" from "resolved". The retry branch then has no reveal to hand out
 *    because it never called this function.
 *
 * 3. STRUCTURAL, ON THE FRAME: `rung.result` is a UNION. The `status: 'retry'`
 *    variant — the only frame that describes a rung still in play — declares
 *    `reveal?: never`, so assigning a reveal to it is an error. There is no
 *    other server frame with a `reveal` member: not `round.arm`, not
 *    `round.key`, not `match.sync`. `protocol.test.ts` asserts that by
 *    exhausting `ServerMessage`.
 *
 * WHAT IS NOT ENFORCED BY THE TYPE, stated plainly rather than papered over:
 * `sealReveal` cannot verify that the verdict it was handed is real — at some
 * point the chain grounds out in the server choosing to call it. What the
 * types remove is every ACCIDENTAL path: you cannot leak a reveal by adding a
 * field, by forgetting a branch, or by attaching one to the wrong frame. The
 * deliberate path is a single function call in a single file, which is a thing
 * a reviewer can actually watch.
 *
 * Runtime backs it up on the receiving side too: `parseServerMessage` rejects
 * a retry frame that carries a reveal, and rejects a reveal whose `rung` does
 * not match the frame's. A buggy or hostile server cannot smuggle one past a
 * client that validates.
 */
export type AnswerReveal = RevealBody & { readonly [RUNG_RESOLVED]: 'rung-resolved' };

/**
 * Evidence that a rung is decided and cannot be answered again.
 *
 * `final` is the literal `true` on purpose — see mechanism 2 above. A pinpoint
 * with attempts left is NOT final and has no verdict to build.
 */
export interface RungVerdict {
  readonly rung: number;
  readonly correct: boolean;
  /** No further submission is possible for this rung. */
  readonly final: true;
}

/**
 * Mint an `AnswerReveal`. The ONLY way to obtain one.
 *
 * Throws when the verdict and the body disagree about which rung they are
 * talking about — a mismatch there is how a reveal for rung 4 would end up
 * attached to a rung-2 result, which would leak an answer that is still in
 * play.
 */
export function sealReveal(verdict: RungVerdict, body: RevealBody): AnswerReveal {
  if (verdict.rung !== body.rung) {
    throw new Error(
      `sealReveal: verdict is for rung ${verdict.rung} but the reveal is for rung ${body.rung}`,
    );
  }
  if (verdict.final !== true) {
    // Unreachable through the type, reachable through a cast. Cheap to check.
    throw new Error(`sealReveal: rung ${verdict.rung} is not final`);
  }
  return body as AnswerReveal;
}

// ---------------------------------------------------------------------------
// S -> C
// ---------------------------------------------------------------------------

export interface WelcomeMessage {
  readonly t: 'welcome';
  readonly v: number;
  readonly playerId: string;
  readonly handle: string;
  /** Server clock at send. Seeds the offset estimate before the first ping. */
  readonly serverTs: number;
  /** HMAC-signed, used to resume after a drop (spec 8.6). */
  readonly resumeToken: string;
}

export interface PongMessage {
  readonly t: 'pong';
  /** Echoed verbatim from the ping. */
  readonly t0: number;
  /** Server receive instant. */
  readonly t1: number;
}

export interface QueuedMessage {
  readonly t: 'queued';
  readonly tier: Tier;
  readonly gameMode?: GameMode;
  readonly queuedAtServerTs: number;
  readonly estimatedWaitMs: number;
  /** Group queue only: how many seats the table will be filled to. */
  readonly groupTargetSeats?: number;
  /**
   * Group queue only: the instant this stops waiting for a second human and
   * starts a duel instead.
   *
   * Sent ONCE, as an absolute server instant, so the client counts down from
   * its own synced clock rather than needing a queue-progress push channel it
   * would otherwise have to grow. Telling the player what is about to happen
   * before it happens is the entire point — a group queue that silently
   * produces a duel reads as the matchmaker being broken.
   */
  readonly groupFallbackAtServerTs?: number;
}

export interface MatchedMessage {
  readonly t: 'matched';
  readonly matchId: string;
  readonly opponent: OpponentInfo;
  readonly tier: Tier;
  readonly gameMode?: GameMode;
  /**
   * The clock this match runs on. Absent means standard.
   *
   * SENT BECAUSE THE CLIENT BUILDS ITS OWN MACHINE from this frame, and that
   * machine draws the ring the player is watching. The room is the authority
   * on when a rung expires; a client left to assume the default would count
   * down four times too fast and look exactly like the game cheating.
   */
  readonly pace?: Pace;
  readonly kind: MatchKind;
  readonly courseId: string | null;
  readonly roundCount: number;
  readonly resumeToken: string;
  /**
   * Server clock at send — lets a resuming client re-derive its offset without
   * waiting for a ping cycle. The match SEED is deliberately absent: handing it
   * over would let a patched client regenerate the answer key (invariant 5).
   */
  readonly serverTs: number;
  /**
   * CHANGE 6. Which round index is GOLDEN, or null when none is.
   *
   * Shipped in the clear, and it leaks nothing: golden-ness is a property of
   * the ROUND NUMBER, not of the country or the question. What it buys is
   * anticipation — the ladder can light the round up on the match card, rounds
   * ahead of time, which is what "it should feel like an event" asks for.
   *
   * Derived from the MATCH SEED, so both seats see the same index. It is not
   * the seed and cannot be inverted to it.
   *
   * Optional so a server that has not computed one yet simply omits it;
   * absent and null both mean "no golden round announced".
   */
  readonly goldenRoundIdx?: number | null;
}

export interface RoundArmMessage {
  readonly t: 'round.arm';
  readonly roundIdx: number;
  /** Both clients reveal at their local estimate of this server instant. */
  readonly startAtServerTs: number;
  /** base64 AES-GCM ciphertext of the round payload (spec 8.2). */
  readonly payloadCiphertext: string;
  /** base64 initialisation vector for the ciphertext. */
  readonly iv: string;
  /**
   * CHANGE 6. This round is the golden one. IN THE CLEAR, deliberately: the
   * arm window is the "before" half of "unmistakable before and during", and
   * the flag says nothing about the country. Absent means false.
   */
  readonly golden?: boolean;
  /**
   * Extra seconds rung 1 runs for, because its prompt is a sentence rather
   * than a name. See `pinpointReadSeconds`. Absent means none.
   *
   * IN THE CLEAR, for the same reason `golden` is, and it has to be. The
   * client's shell plan carries no prompts, so the client cannot derive this;
   * and the reveal is a RACE between `local.reveal` and the tick that fires
   * the armed deadline, so a value that only arrived with the payload would be
   * missing exactly when the tick won. The client would then run a clock five
   * seconds shorter than the server's and bust a round the server still had
   * open, which is the worst-looking bug this field could have.
   *
   * What it gives away is that this round's prompt is long, half a second
   * early. It names no place, and there is nothing to do with it.
   */
  readonly readSeconds?: number;
  /**
   * THIS SEAT is playing the round under an eclipse: night-side globe, no
   * borders. Absent means false.
   *
   * PER SEAT, and the only field on this frame that is deliberately not the
   * same for everybody. Every other asymmetry here is a fairness bug; this one
   * is the mechanic — an eclipse that fell on the whole table every time would
   * not be a bounty on the leader, it would be a weather report.
   *
   * In the clear, like `golden`, and for the same reason: the client has to
   * dress the globe before the reveal instant, and the flag says nothing about
   * which country is coming.
   */
  readonly eclipsed?: boolean;
  /**
   * This round is the eclipse round: what happens after it is decided by who
   * climbs highest. In the clear so the arm window can say so — a handicap
   * nobody saw coming reads as a fault, which is the one thing it must not.
   */
  readonly eclipseRound?: boolean;
  /**
   * Opaque handles for this round's image artwork, IN THE CLEAR and SHUFFLED.
   *
   * They are in the clear because they have to be: the whole point is that the
   * client can pull the bytes down during the arm window, so that at the reveal
   * instant both players already hold them and spec 8.2's one-server-instant
   * guarantee covers the pixels too, not just the prompt (invariant 2). A
   * token is 128 bits of randomness minted per round PER SEAT; it names no
   * country, no file, no ISO code, and nothing about it is stable between two
   * rounds, two seats, or two matches.
   *
   * What comes back is itself sealed, with the SAME key as the rung whose
   * options the artwork belongs to — so preloading buys the bytes and not one
   * bit of the answer. A client that fetches these at T-500ms holds four
   * flags it cannot look at until it has committed to the rung that shows
   * them, which is exactly the deal spec 8.2 already strikes for the prompt.
   *
   * Ordered by rung, and matched by the `assetIdx` inside each rung's sealed
   * blob — an order that reveals nothing the payload's own `probeType` per
   * rung does not already say, and that survives the resync replay unchanged.
   */
  readonly assetTokens?: readonly string[];
}

export interface RoundKeyMessage {
  readonly t: 'round.key';
  readonly roundIdx: number;
  /**
   * Which rung this key unseals. 1 opens the round envelope at T+0; 2..N open
   * one inline-sealed prompt each, released only once the player has committed
   * to that rung.
   *
   * A key used to arrive with nothing but a roundIdx, which meant the only way
   * to tell two of them apart was to try decrypting with each and let the
   * AES-GCM tag arbitrate. That is how "every online push busts" shipped: the
   * second key for a round is a DIFFERENT kind of thing from the first, and
   * nothing in the frame said so. `parseServerMessage` REQUIRES this field on
   * the wire (see below) — it is optional only in the type, because the
   * reducer's own tests construct synthetic keys and `@geogame/rules` ignores
   * the field entirely.
   */
  readonly rung?: number;
  /** base64 raw AES-GCM key, broadcast at T+0 and not one millisecond sooner. */
  readonly key: string;
}

interface RungResultBase {
  readonly t: 'rung.result';
  readonly roundIdx: number;
  readonly rung: number;
  /** Server-measured latency after the 8.3 clamp. */
  readonly latencyMs: number;
  /** This rung belongs to the golden round. Absent means false. */
  readonly golden?: boolean;
}

/**
 * The rung is DECIDED. Nothing further can be submitted for it, so the answer
 * key is safe to ship (change 3) and the reveal belongs here and nowhere else.
 *
 * `status` is optional on this variant and required on the retry one, which
 * makes "resolved" the default reading of a `rung.result` and forces the
 * exceptional case — a rung still in play — to announce itself. Narrowing
 * still works both ways because `'resolved' | undefined` and `'retry'` are
 * disjoint.
 *
 * `reveal` is optional because a rung can resolve with nothing to reveal (a
 * forfeit, a synthetic frame in the reducer's own tests). The guarantee this
 * type carries is the CONVERSE, and it is the one that matters: a frame for a
 * rung still in play cannot carry a reveal at all. In normal server operation
 * `reveal` is always populated — that is change 3's whole point.
 */
export interface RungResolvedMessage extends RungResultBase {
  readonly status?: 'resolved';
  readonly correct: boolean;
  /** Pot after applying this rung. 0 when the rung busted. */
  readonly pot: number;
  /** False at ladder depth, or when the rung busted. */
  readonly canPush: boolean;
  /** Post-resolution only, and structurally impossible to forge — `sealReveal`. */
  readonly reveal?: AnswerReveal;
  /** Present on rung 1 when the tier allows retries: how the attempts went. */
  readonly attempts?: PinpointAttemptState;
  /** For a numeric rung, which credit band the submission landed in. */
  readonly numericBand?: NumericCreditBand;
}

/**
 * CHANGE 4 + CHANGE 2. The pinpoint was WRONG and the rung is STILL IN PLAY —
 * attempts remain.
 *
 * This is the frame that makes immediate pinpoint feedback possible without
 * breaking invariant 5. It confirms the player's OWN pick (which they already
 * know — they just tapped it) so the globe can flash it red, and it says what
 * the next attempt is worth so the retry is an informed choice. It says
 * NOTHING about where the right answer is.
 *
 * `reveal?: never` is not decoration: it is what stops a future edit attaching
 * the target to a rung the player is still betting on.
 */
export interface RungRetryMessage extends RungResultBase {
  readonly status: 'retry';
  /** A retry frame exists only because the attempt was wrong. */
  readonly correct: false;
  /** Nothing is banked while the rung is unresolved. */
  readonly pot: 0;
  /** You cannot push out of a rung you have not cleared. */
  readonly canPush: false;
  readonly attempts: PinpointAttemptState;
  /**
   * The country the PLAYER tapped, echoed back so the globe can highlight it
   * red. Their own input; it leaks nothing. Null for an ocean tap.
   */
  readonly pickedCca3: string | null;
  /**
   * INVARIANT 2. Milliseconds to add to the rung-1 deadline because of this
   * retry, capped at `WINDOW_RTT_CREDIT_CAP_MS`. Absent means zero.
   *
   * A retry is the ONLY place in the game where a player has to complete
   * several round trips inside one fixed window. Rung 1's deadline is set once
   * at reveal and enforced on ARRIVAL, so every retry costs a full RTT the
   * player cannot avoid: the tap flies out, and the verdict has to fly home
   * before they can decide where to tap next. Left uncredited, think time
   * across three attempts is `rungMs - 2.5R` against `rungMs - 0.5R` for one,
   * and an 800ms link is denied the third attempt change 4 exists to give it:
   *
   *   p_a rtt=  0ms  4.6s of thought per attempt -> ok, ok, ok
   *   p_b rtt=800ms  4.6s of thought per attempt -> ok, ok, round_closed
   *
   * One credited round trip per retry restores parity exactly: the verdict's
   * flight home plus the next tap's flight out. This is the same credit
   * `pushTickServerTs` already gives a deeper rung, arriving at the transition
   * change 4 added and nobody wired.
   *
   * The reducer applies it, so client and server move the same deadline by the
   * same amount from the same frame (invariant 6), and `match.sync` replays
   * the already-extended deadline rather than recomputing it.
   */
  readonly deadlineCreditMs?: number;
  /** Structurally absent. See `AnswerReveal`, mechanism 3. */
  readonly reveal?: never;
}

export type RungResultMessage = RungResolvedMessage | RungRetryMessage;

/** True when this frame describes a rung that can still be answered. */
export function isRungInPlay(msg: RungResultMessage): msg is RungRetryMessage {
  return msg.status === 'retry';
}

export type OppPhase =
  | 'arming'
  | 'pinpoint'
  | 'deciding'
  | 'probe'
  | 'banked'
  | 'busted'
  | 'idle';

export interface OppStateMessage {
  readonly t: 'opp.state';
  /**
   * WHICH rival this frame is about.
   *
   * A duel never needed it: there was exactly one other seat, so "the
   * opponent" was unambiguous and the handle came from `matched` once. A group
   * match picks a SUBJECT PER RECIPIENT — the best of everyone who is not you
   * — and that subject changes between rounds as the standings move. Without a
   * name on the frame the ladder would silently retitle itself.
   */
  readonly playerId: string;
  readonly handle: string;
  readonly roundIdx: number;
  readonly rung: number;
  readonly banked: boolean;
  /** Opponent's banked total — always visible; this is the anxiety engine. */
  readonly total: number;
  readonly phase: OppPhase;
  /**
   * Highest rung reached this round by ANY seat other than the recipient.
   *
   * A HIGH-WATER MARK, NOT A LIVE MAXIMUM, and that distinction is the whole
   * reason the field is expressible in one number. `rung` above belongs to one
   * named rival, fixed at round start so the ladder stays coherent; this is
   * the pressure from everyone else, which would otherwise need a ladder per
   * opponent and does not fit on a phone.
   *
   * Monotonic within a round by construction. A live maximum would FALL when
   * the leader busts, and an opponent ladder that un-climbs reads as a bug
   * rather than as drama — the player did nothing, so nothing should move.
   *
   * In a two-seat room this is exactly the rival's own high-water mark, so a
   * client can simply hide it whenever it does not exceed `rung`.
   */
  readonly fieldHighRung: number;
  /**
   * ONLY populated once the RECEIVING player has locked their own rung-1
   * pinpoint for `roundIdx`. Undefined at every other moment. Sending this
   * early leaks the answer.
   */
  readonly lngLat?: LngLat;
}

/**
 * The one-line fact the post-round reveal card teaches, computed by the SERVER.
 *
 * INVARIANT 5, and the reason this is on the wire rather than looked up in the
 * client. The card wants a capital, a population, a border count or a language
 * list — every one of which is an answer key for some other round — and a
 * client that can render the card at round end is a client that HELD the whole
 * fact table from the moment it loaded. That is precisely how a shipped bundle
 * became a complete answer key. The server knows the fact, the round is over,
 * and one prose string crosses the wire instead of two hundred records.
 *
 * A string rather than fields, deliberately: a struct invites "just send the
 * population too, the round is over" for a round that is over while six others
 * are not.
 */
export interface RoundFact {
  readonly cca3: string;
  /** Display name; the client's identity table has this too, this is belt-and-braces. */
  readonly name: string;
  /** One sentence, already formatted. Empty when the dataset has nothing to say. */
  readonly line: string;
}

export interface RoundEndMessage {
  readonly t: 'round.end';
  readonly roundIdx: number;
  readonly you: RoundSideSummary;
  readonly opp: RoundSideSummary;
  /** Safe now: the round is resolved for both seats. */
  readonly targetLngLat: LngLat;
  /** City name in city mode. Safe now: the round is resolved. */
  readonly targetLabel?: string;
  /** This was the golden round. Absent means false. */
  readonly golden?: boolean;
  /** The reveal card's teaching line. Safe now: the round is resolved. */
  readonly fact?: RoundFact;
}

/**
 * One seat's live round, as the server holds it. Every field is something this
 * seat had already seen before its socket dropped — no answer key, no seed, no
 * opponent pin.
 */
export interface MatchSyncRound {
  readonly index: number;
  readonly rung: number;
  readonly phase: MatchPhase;
  readonly pot: number;
  readonly banked: boolean;
  readonly outcome: RoundOutcome | null;
  readonly pinLngLat: LngLat | null;
  readonly pinCca3: string | null;
  readonly decision: Decision | null;
  readonly rungStartedAtServerTs: number | null;
  readonly deadlineServerTs: number | null;
  /** Pinpoint attempts already spent this round (change 4). Absent means 0. */
  readonly pinpointAttempt?: number;
  /** This round is golden (change 6). Absent means false. */
  readonly golden?: boolean;
}

/**
 * Seat state replay on reconnect — spec 8.6, "server replays current state".
 *
 * Sent only from `resync`, after `matched` and the re-armed round, because
 * `matched` is what a client rebuilds its match around and this is what tells
 * it what that match currently IS. Without it a resumed client shows a zeroed
 * score and the globe for a round the server already has in `decision`.
 *
 * The round clock is NOT restarted: `deadlineServerTs` is the original one, so
 * lost time stays lost and the opponent is not punished for the other player's
 * network.
 */
export interface MatchSyncMessage {
  readonly t: 'match.sync';
  readonly matchId: string;
  readonly bankedTotal: number;
  readonly round: MatchSyncRound | null;
  /** Rounds already resolved, in order — the `round.end` frames this seat got. */
  readonly history: readonly RoundEndMessage[];
  /** Same value `matched` carried; repeated so a resync needs one frame less. */
  readonly goldenRoundIdx?: number | null;
}

export interface MatchEndMessage {
  readonly t: 'match.end';
  readonly matchId: string;
  readonly scores: readonly PlayerScore[];
  readonly winnerPlayerId: string | null;
  readonly ratingDelta: RatingDelta | null;
  /** Single-attempt friend-duel link token. */
  readonly shareToken: string | null;
}

export const ERROR_CODES = [
  'bad_message',
  'bad_version',
  'unauthorized',
  'rate_limited',
  'not_in_match',
  'round_closed',
  'already_answered',
  /** Change 4: the pinpoint allowance for this round is spent. */
  'no_attempts_left',
  'queue_failed',
  'share_token_consumed',
  'daily_already_played',
  'forfeited',
  'internal',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ErrorMessage {
  readonly t: 'error';
  readonly code: ErrorCode;
  readonly message: string;
  /** When true the socket is about to close. */
  readonly fatal: boolean;
}

export interface LobbyMember {
  readonly playerId: string;
  readonly handle: string;
}

/**
 * Who is currently standing in a link's lobby.
 *
 * WHY A LOBBY EXISTS AT ALL. A duel link used to drop both ends straight into
 * the matchmaker, where they paired only if they were queued during the same
 * sweep — an eight-second window before the first arrival was given a ghost
 * and the link was spent. Two friends on a phone call could not reliably hit
 * it. A lobby replaces the coincidence with a room they wait in: it holds
 * until somebody presses start, and the roster is what tells them it is worth
 * pressing.
 *
 * Re-sent to every member on every join and leave, so the roster is a fact
 * rather than something the client accumulates.
 */
export interface LobbyStateMessage {
  readonly t: 'lobby.state';
  readonly duelId: string;
  readonly tier: Tier;
  readonly gameMode: GameMode;
  readonly courseId: string | null;
  /** Seats this link admits. Every friend link is minted at the maximum. */
  readonly capacity: number;
  /**
   * Who owns the settings. Always a seated member: it transfers automatically
   * when the holder leaves, so this is never an id the roster cannot show.
   */
  readonly hostId: string;
  /** What the host has set the clock to. Everybody in the room plays it. */
  readonly pace: Pace;
  readonly members: readonly LobbyMember[];
}

export type ServerMessage =
  | WelcomeMessage
  | PongMessage
  | QueuedMessage
  | LobbyStateMessage
  | MatchedMessage
  | RoundArmMessage
  | RoundKeyMessage
  | RungResultMessage
  | OppStateMessage
  | RoundEndMessage
  | MatchSyncMessage
  | MatchEndMessage
  | ErrorMessage;

export type ServerMessageType = ServerMessage['t'];

/** Distributes over a union and keeps only the members that declare `reveal`. */
type WithRevealKey<T> = T extends unknown ? ('reveal' extends keyof T ? T : never) : never;

/**
 * COMPILE-TIME assertion for mechanism 3 above: no server frame other than
 * `rung.result` declares a `reveal` member.
 *
 * If somebody adds `reveal` to `round.arm` or `match.sync`, `Exclude` stops
 * being `never`, this alias becomes `never`, and the `= true` below stops
 * compiling. It costs one boolean at runtime and it fails at the exact edit
 * that would break the invariant — which a test cannot do, because a test can
 * only check the frames somebody remembered to write.
 */
export type NoRevealOutsideRungResult =
  Exclude<WithRevealKey<ServerMessage>, RungResultMessage> extends never ? true : never;

export const NO_REVEAL_OUTSIDE_RUNG_RESULT: NoRevealOutsideRungResult = true;

// ---------------------------------------------------------------------------
// Round payload — the plaintext inside `payloadCiphertext`
// ---------------------------------------------------------------------------

export interface RoundPayloadOption {
  readonly id: string;
  /**
   * Visible text, EMPTY for an image option.
   *
   * It used to carry the country's regional-indicator emoji next to an
   * `imageUrl` of `https://flagcdn.com/<iso2>.svg`. Both spell the ISO code,
   * and a flag rung's prompt names the target, so the pair was `correctIndex`
   * in plain text on the terminal rung of the ladder. Neither field exists any
   * more: the artwork arrives as bytes, and where an option shows a picture it
   * says nothing at all.
   */
  readonly label: string;
}

interface RoundPayloadRungBase {
  readonly rung: number;
  readonly prompt: string;
  /** This rung is the golden round's terminal rung (change 6). */
  readonly golden?: boolean;
}

/**
 * Rung 1. Answered on the globe, not with options, and now with up to
 * `RoundPayload.pinpointAttempts.maxAttempts` tries.
 */
export interface PinpointRungPayload extends RoundPayloadRungBase {
  readonly kind: 'pinpoint';
  readonly probeType: 'pinpoint';
}

/**
 * A deeper rung whose prompt is still CIPHERTEXT (spec 8.2). `prompt` is the
 * sealed blob; the key arrives only once the player commits to the rung.
 *
 * `answerKind` rides in the clear so the client can pre-build the right input
 * control — a digit wheel takes longer to mount than four buttons, and change
 * 6 explicitly refuses to make a phone player wait on that under a running
 * clock. It leaks nothing: `probeType` was already in the clear here, and
 * neither says anything about the country.
 */
export interface SealedRungPayload extends RoundPayloadRungBase {
  readonly kind: 'sealed';
  readonly probeType: ProbeType;
  readonly answerKind: AnswerKind;
}

/** Ordinary multiple choice: `PROBE_OPTION_COUNT` options, exactly one right. */
export interface ChoiceRungPayload extends RoundPayloadRungBase {
  readonly kind: 'choice';
  readonly probeType: ProbeType;
  readonly options: readonly RoundPayloadOption[];
  /**
   * Present exactly when this rung's options are pictures: an index into the
   * `assetTokens` its `round.arm` shipped in the clear. The bundle behind that
   * token holds one image per option, in option order.
   *
   * ONE BUNDLE, not one handle per option: four handles mean four responses,
   * and four responses have four lengths — and the artwork is committed, so a
   * length is a public fingerprint of the country. One padded response has one
   * length, and it says only how big the four flags were together.
   *
   * AN INDEX, not the token itself, because the sealed payload has to stay
   * BYTE-IDENTICAL for both seats — that is the property that makes the arm
   * cost the same on both pipes (spec 8.2) — while the tokens must NOT be
   * shared between seats. So the shape is per-seat outside the seal, aligned
   * by position inside it. This field lives inside the rung's sealed blob, so
   * even the mapping is unreadable until the player commits to the rung.
   */
  readonly assetIdx?: number;
  /**
   * A MAP CHOICE: the four options are dots on the globe rather than buttons,
   * and this is where they are, IN OPTION ORDER. Present only for the
   * subdivision mode's rung 2 ("which of these is the state capital?").
   *
   * The options themselves carry empty labels, exactly as a flag rung's do —
   * an option that is a picture has no text, and an option that is a place on
   * the map has none either. `names` carries what the four places are CALLED.
   */
  readonly markers?: readonly LngLat[];
  /**
   * The candidate names, SORTED, for a rung whose options are `markers`.
   *
   * Sorted is the whole design. A player has to be told the four names or the
   * question is "point at a capital, any capital"; they must NOT be told which
   * name goes with which dot, because for anyone who knows their state
   * capitals that pairing IS the answer. Alphabetical order is independent of
   * the shuffled option order, so the two arrays together say nothing that
   * either says alone.
   *
   * The pairing arrives with the reveal, on `RevealBody.choice.labels`, once
   * the rung is dead.
   */
  readonly names?: readonly string[];
}

/**
 * CHANGE 5. A statement about the country, answered true or false. Exactly two
 * options; their `id`s are `TRUE_OPTION_ID` / `FALSE_OPTION_ID`, so the client
 * can label them correctly whatever order they arrive in.
 */
export interface BooleanRungPayload extends RoundPayloadRungBase {
  readonly kind: 'boolean';
  readonly probeType: ProbeType;
  readonly options: readonly [RoundPayloadOption, RoundPayloadOption];
}

/**
 * An ordering task. `options` carries the rows AS SHUFFLED; the player
 * reorders them and the submitted permutation rides `AnswerValue` as a Lehmer
 * rank (`encodeOrdering` in `@geogame/rules`).
 */
export interface OrderingRungPayload extends RoundPayloadRungBase {
  readonly kind: 'ordering';
  readonly probeType: ProbeType;
  readonly options: readonly RoundPayloadOption[];
}

/**
 * CHANGE 6. The golden round's open-ended numeric rung. NO OPTIONS AT ALL —
 * a 0% blind-guess floor, which is the entire point.
 *
 * `numeric` is a reference into `NUMERIC_SPECS` and is answer-independent; see
 * the invariant-5 note on `NumericAnswerSpec`.
 */
export interface NumericRungPayload extends RoundPayloadRungBase {
  readonly kind: 'numeric';
  readonly probeType: ProbeType;
  readonly numeric: NumericAnswerSpec;
}

/**
 * A rung as the client receives it. Discriminated on `kind` (change 5, change
 * 6): 2 options, 4 options, an ordering, or none at all are genuinely
 * different shapes, and this is the union that says so.
 *
 * Note what is NOT in ANY variant: no correctIndex, no correctOrder, no
 * correctValue, no target cca3. Invariant 5.
 */
export type RoundPayloadRung =
  | PinpointRungPayload
  | SealedRungPayload
  | ChoiceRungPayload
  | BooleanRungPayload
  | OrderingRungPayload
  | NumericRungPayload;

/** The variants that carry an `options` array. Handy for renderers. */
export type OptionBearingRung = ChoiceRungPayload | BooleanRungPayload | OrderingRungPayload;

export function rungHasOptions(r: RoundPayloadRung): r is OptionBearingRung {
  return r.kind === 'choice' || r.kind === 'boolean' || r.kind === 'ordering';
}

/** The rung's options, or undefined for a pinpoint, sealed or numeric rung. */
export function rungOptions(r: RoundPayloadRung): readonly RoundPayloadOption[] | undefined {
  return rungHasOptions(r) ? r.options : undefined;
}

/** The rung's artwork bundle index, or undefined when it shows no pictures. */
export function rungAssetIdx(r: RoundPayloadRung): number | undefined {
  return r.kind === 'choice' ? r.assetIdx : undefined;
}

/**
 * Encrypted round payload. Note what is NOT here: no correctIndex, no target
 * cca3, no coordinates. A fully decompiled client that decrypts this early
 * still cannot answer rung 1 (invariant 5, spec 8.2).
 */
export interface RoundPayload {
  readonly roundIdx: number;
  readonly gameMode?: GameMode;
  readonly prompt: string;
  readonly ladderDepth: number;
  readonly secondsPerRung: number;
  readonly decisionSeconds: number;
  /** Rung 1 metadata only; deeper rungs arrive as the player pushes. */
  readonly rungs: readonly RoundPayloadRung[];
  /** Explorer tier pre-orients the globe to this region (spec section 3). */
  readonly orientRegion?: string;
  /**
   * CHANGE 4. The pinpoint allowance for this round, from the server rather
   * than from the client's copy of TIER_CONFIG.
   *
   * It is derivable from the tier, and the client HAS the tier — but the
   * server is the authority on scoring and a config skew between the two would
   * show the player one price and charge another. Change 4 requires the price
   * be visible BEFORE the first tap, so it has to be in the payload, which is
   * open at T+0.
   */
  readonly pinpointAttempts: PinpointAttemptPolicy;
  /**
   * CHANGE 6. This round is the golden one — its terminal rung is an open
   * numeric answer paying `GOLDEN_MULTIPLIER_FACTOR` times the normal terminal
   * multiplier. Absent means false.
   *
   * Also flagged in the clear on `round.arm` so the ladder can light up during
   * the arm window, before this payload opens.
   */
  readonly golden?: boolean;
}

// ---------------------------------------------------------------------------
// Answer event log — invariant 8. Cannot be generated retroactively.
// ---------------------------------------------------------------------------

export interface AnswerEvent {
  readonly runId: string;
  readonly playerId: string;
  readonly roundIdx: number;
  readonly rung: number;
  readonly probeType: ProbeType | 'pinpoint';
  readonly entityCode: string;
  readonly correct: boolean;
  readonly latencyMs: number;
  readonly decision: Decision | null;
  readonly createdAtServerTs: number;
  readonly optionIdx: number | null;
  /**
   * The submitted NUMBER for an open-numeric rung, where `optionIdx` is null
   * and would otherwise lose the answer entirely (invariant 8: every answer
   * logged). Null or absent for every other kind.
   */
  readonly numericValue?: number | null;
  /** Which pinpoint attempt this row is, 1-based. Absent for probe rungs. */
  readonly attempt?: number;
  /** This row belongs to the golden round. */
  readonly golden?: boolean;
}

// ---------------------------------------------------------------------------
// Runtime validation
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function int(v: unknown): v is number {
  return num(v) && Number.isInteger(v);
}

function str(v: unknown): v is string {
  return typeof v === 'string';
}

function optStr(v: unknown): v is string | undefined {
  return v === undefined || typeof v === 'string';
}

function isLngLat(v: unknown): v is LngLat {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    num(v[0]) &&
    num(v[1]) &&
    v[0] >= -180 &&
    v[0] <= 180 &&
    v[1] >= -90 &&
    v[1] <= 90
  );
}

const CLIENT_TYPES = new Set<string>([
  'hello',
  'ping',
  'queue',
  'lobby.start',
  'lobby.configure',
  'lobby.promote',
  'rename',
  'dequeue',
  'pinpoint',
  'push',
  'bank',
  'answer',
]);

const SERVER_TYPES = new Set<string>([
  'welcome',
  'pong',
  'queued',
  'lobby.state',
  'matched',
  'round.arm',
  'round.key',
  'rung.result',
  'opp.state',
  'round.end',
  'match.sync',
  'match.end',
  'error',
]);

export function isClientMessageType(v: unknown): v is ClientMessageType {
  return typeof v === 'string' && CLIENT_TYPES.has(v);
}

export function isServerMessageType(v: unknown): v is ServerMessageType {
  return typeof v === 'string' && SERVER_TYPES.has(v);
}

const TIER_SET = new Set<string>(['explorer', 'navigator', 'cartographer', 'surveyor']);
const GAME_MODE_SET = new Set<string>(['country', 'city', 'state']);

/**
 * Narrow an untrusted string to a `GameMode`.
 *
 * The mirror of `isTier`, and it exists for the same reason: a game mode now
 * arrives from places the wire parser never sees — a signed duel token's `gm`
 * claim, a REST body — and each of those was otherwise about to grow its own
 * copy of this list.
 */
export function isGameMode(v: unknown): v is GameMode {
  return typeof v === 'string' && GAME_MODE_SET.has(v);
}

/**
 * An asset token as it is allowed to appear on the wire: base64url, 22
 * characters, which is exactly 128 bits with no padding. Pinned as a regex
 * rather than "any string" because the client turns these into request paths.
 */
export const ASSET_TOKEN_RE = /^[A-Za-z0-9_-]{22}$/;

/** One bundle per image-bearing rung, and a ladder is at most four rungs deep. */
export const MAX_ASSET_TOKENS_PER_ARM = 8;

/**
 * Structural validation of an inbound C->S message. Returns null for anything
 * malformed — the server must treat null as `error{code:'bad_message'}` and
 * never as a partially-trusted object.
 */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(value)) return null;
  const t = value['t'];
  if (!isClientMessageType(t)) return null;

  switch (t) {
    case 'hello':
      if (!int(value['v'])) return null;
      if (!optStr(value['resumeToken']) || !optStr(value['handle'])) return null;
      return value as unknown as HelloMessage;

    case 'ping':
      if (!num(value['t0'])) return null;
      return value as unknown as PingMessage;

    case 'queue': {
      const tier = value['tier'];
      if (!str(tier) || !TIER_SET.has(tier)) return null;
      if (!optStr(value['courseId']) || !optStr(value['shareToken'])) return null;
      const kind = value['kind'];
      if (
        kind !== undefined &&
        kind !== 'quick' &&
        kind !== 'friend' &&
        kind !== 'daily' &&
        kind !== 'group'
      ) {
        return null;
      }
      const gameMode = value['gameMode'];
      if (gameMode !== undefined && (!str(gameMode) || !GAME_MODE_SET.has(gameMode))) return null;
      return value as unknown as QueueMessage;
    }

    case 'lobby.start':
      return { t: 'lobby.start' };

    case 'rename': {
      const handle = value['handle'];
      if (!str(handle)) return null;
      return { t: 'rename', handle };
    }

    case 'lobby.configure': {
      const tier = value['tier'];
      if (tier !== undefined && (!str(tier) || !TIER_SET.has(tier))) return null;
      const gameMode = value['gameMode'];
      if (gameMode !== undefined && (!str(gameMode) || !GAME_MODE_SET.has(gameMode))) return null;
      const courseId = value['courseId'];
      if (courseId !== undefined && courseId !== null && !str(courseId)) return null;
      const pace = value['pace'];
      if (pace !== undefined && !isPace(pace)) return null;
      return {
        t: 'lobby.configure',
        ...(tier === undefined ? {} : { tier: tier as Tier }),
        ...(gameMode === undefined ? {} : { gameMode: gameMode as GameMode }),
        ...(courseId === undefined ? {} : { courseId: courseId as string | null }),
        ...(pace === undefined ? {} : { pace: pace as Pace }),
      };
    }

    case 'lobby.promote': {
      const playerId = value['playerId'];
      if (!str(playerId)) return null;
      return { t: 'lobby.promote', playerId };
    }

    case 'dequeue':
      return { t: 'dequeue' };

    case 'pinpoint': {
      if (!int(value['roundIdx']) || !num(value['clientTs'])) return null;
      const code = value['entityCode'];
      if (code !== null && !str(code)) return null;
      if (!isLngLat(value['lngLat'])) return null;
      const attempt = value['attempt'];
      if (attempt !== undefined && (!int(attempt) || attempt < 1)) return null;
      return value as unknown as PinpointMessage;
    }

    case 'push':
    case 'bank':
      if (!int(value['roundIdx']) || !num(value['clientTs'])) return null;
      return value as unknown as PushMessage | BankMessage;

    case 'answer':
      if (!int(value['roundIdx']) || !int(value['rung'])) return null;
      if (!num(value['clientTs'])) return null;
      // The tagged union is the ONLY accepted shape. A bare `optionIdx` is a
      // v1 client and is rejected here rather than silently mis-graded; the
      // version gate in `hello` should have caught it first.
      if (!isAnswerValue(value['answer'])) return null;
      return value as unknown as AnswerMessage;

    default:
      return null;
  }
}

/** Structural check of a reveal body. Shape only — it cannot verify the truth. */
function isRevealBody(v: unknown): v is RevealBody {
  if (!isRecord(v)) return false;
  if (!int(v['rung'])) return false;
  switch (v['kind']) {
    case 'pinpoint':
      if (!str(v['correctCca3']) || !isLngLat(v['correctLngLat'])) return false;
      if (!optStr(v['correctLabel'])) return false;
      if (v['pickedCca3'] !== null && !str(v['pickedCca3'])) return false;
      if (v['pickedLngLat'] !== null && !isLngLat(v['pickedLngLat'])) return false;
      return true;
    case 'choice': {
      if (!int(v['correctIndex'])) return false;
      if (v['pickedIndex'] !== null && !int(v['pickedIndex'])) return false;
      const labels = v['labels'];
      if (labels !== undefined && (!Array.isArray(labels) || !labels.every((x) => str(x)))) {
        return false;
      }
      return true;
    }
    case 'boolean':
      if (!int(v['correctIndex']) || typeof v['statementIsTrue'] !== 'boolean') return false;
      if (v['pickedIndex'] !== null && !int(v['pickedIndex'])) return false;
      return true;
    case 'ordering': {
      const correct = v['correctOrder'];
      if (!Array.isArray(correct) || !correct.every((x) => int(x))) return false;
      const picked = v['pickedOrder'];
      if (picked !== null && (!Array.isArray(picked) || !picked.every((x) => int(x)))) return false;
      return true;
    }
    case 'numeric':
      if (!isNumericQuantity(v['quantity']) || !num(v['correctValue'])) return false;
      if (v['pickedValue'] !== null && !num(v['pickedValue'])) return false;
      return (NUMERIC_CREDIT_BANDS as readonly unknown[]).includes(v['band']);
    default:
      return false;
  }
}

function isAttemptState(v: unknown): v is PinpointAttemptState {
  if (!isRecord(v)) return false;
  return (
    int(v['attempt']) &&
    int(v['maxAttempts']) &&
    int(v['attemptsRemaining']) &&
    num(v['nextMultiplier']) &&
    num(v['nextMaxPoints'])
  );
}

/**
 * Structural validation of an inbound S->C message. The client uses this so a
 * proxy or a version skew produces a clean failure instead of a crash mid-round.
 */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(value)) return null;
  const t = value['t'];
  if (!isServerMessageType(t)) return null;

  switch (t) {
    case 'welcome':
      if (!int(value['v']) || !str(value['playerId']) || !str(value['handle'])) return null;
      if (!num(value['serverTs']) || !str(value['resumeToken'])) return null;
      return value as unknown as WelcomeMessage;

    case 'pong':
      if (!num(value['t0']) || !num(value['t1'])) return null;
      return value as unknown as PongMessage;

    case 'lobby.state': {
      const tier = value['tier'];
      if (!str(tier) || !TIER_SET.has(tier)) return null;
      const gameMode = value['gameMode'];
      if (!str(gameMode) || !GAME_MODE_SET.has(gameMode)) return null;
      if (!str(value['duelId']) || !int(value['capacity'])) return null;
      if (value['courseId'] !== null && !str(value['courseId'])) return null;
      const members = value['members'];
      if (!Array.isArray(members)) return null;
      for (const m of members) {
        if (!isRecord(m) || !str(m['playerId']) || !str(m['handle'])) return null;
      }
      return value as unknown as LobbyStateMessage;
    }

    case 'queued': {
      const tier = value['tier'];
      if (!str(tier) || !TIER_SET.has(tier)) return null;
      if (!num(value['queuedAtServerTs']) || !num(value['estimatedWaitMs'])) return null;
      const gameMode = value['gameMode'];
      if (gameMode !== undefined && (!str(gameMode) || !GAME_MODE_SET.has(gameMode))) return null;
      if (value['groupTargetSeats'] !== undefined && !int(value['groupTargetSeats'])) return null;
      if (
        value['groupFallbackAtServerTs'] !== undefined &&
        !num(value['groupFallbackAtServerTs'])
      ) {
        return null;
      }
      return value as unknown as QueuedMessage;
    }

    case 'matched': {
      if (!str(value['matchId']) || !isRecord(value['opponent'])) return null;
      const tier = value['tier'];
      if (!str(tier) || !TIER_SET.has(tier)) return null;
      if (!int(value['roundCount']) || !str(value['resumeToken'])) return null;
      if (!num(value['serverTs'])) return null;
      const gameMode = value['gameMode'];
      if (gameMode !== undefined && (!str(gameMode) || !GAME_MODE_SET.has(gameMode))) return null;
      const golden = value['goldenRoundIdx'];
      if (golden !== undefined && golden !== null && (!int(golden) || golden < 0)) return null;
      return value as unknown as MatchedMessage;
    }

    case 'round.arm': {
      for (const flag of ['golden', 'eclipsed', 'eclipseRound'] as const) {
        if (value[flag] !== undefined && typeof value[flag] !== 'boolean') return null;
      }
      if (!int(value['roundIdx']) || !num(value['startAtServerTs'])) return null;
      if (!str(value['payloadCiphertext']) || !str(value['iv'])) return null;
      if (value['golden'] !== undefined && typeof value['golden'] !== 'boolean') return null;
      const tokens = value['assetTokens'];
      if (tokens !== undefined) {
        if (!Array.isArray(tokens) || !tokens.every((x) => str(x))) return null;
        // A preload list is a list of URLs this client is about to request.
        // Bounding its length and each entry's shape here means a hostile
        // frame cannot turn the arm window into a request amplifier.
        if (tokens.length > MAX_ASSET_TOKENS_PER_ARM) return null;
        if (tokens.some((x) => !ASSET_TOKEN_RE.test(x as string))) return null;
      }
      return value as unknown as RoundArmMessage;
    }

    case 'round.key':
      if (!int(value['roundIdx']) || !str(value['key'])) return null;
      // Required ON THE WIRE even though the type marks it optional: a frame
      // that does not say which rung it unseals is not a frame this client
      // knows how to handle safely.
      if (!int(value['rung']) || (value['rung'] as number) < 1) return null;
      return value as unknown as RoundKeyMessage;

    case 'rung.result': {
      if (!int(value['roundIdx']) || !int(value['rung'])) return null;
      if (typeof value['correct'] !== 'boolean' || typeof value['canPush'] !== 'boolean') {
        return null;
      }
      if (!num(value['pot']) || !num(value['latencyMs'])) return null;

      const status = value['status'];
      if (status !== undefined && status !== 'resolved' && status !== 'retry') return null;

      const reveal = value['reveal'];
      if (status === 'retry') {
        // THE RUNTIME HALF OF INVARIANT 5's GUARANTEE. A rung that is still in
        // play may not carry an answer, so a frame that claims both is not a
        // frame this client will act on — it is dropped, not sanitised.
        if (reveal !== undefined) return null;
        if (value['correct'] !== false || value['canPush'] !== false || value['pot'] !== 0) {
          return null;
        }
        if (!isAttemptState(value['attempts'])) return null;
        if (value['pickedCca3'] !== null && !str(value['pickedCca3'])) return null;
        // A deadline credit is WINDOW, and window is exactly what a hostile
        // frame would want unbounded. Same cap the server applies, checked
        // again here so the reducer can never be handed more than one round
        // trip of extra clock per retry.
        const credit = value['deadlineCreditMs'];
        if (credit !== undefined) {
          if (!num(credit) || credit < 0 || credit > WINDOW_RTT_CREDIT_CAP_MS) return null;
        }
        return value as unknown as RungRetryMessage;
      }

      if (reveal !== undefined) {
        if (!isRevealBody(reveal)) return null;
        // The reveal must be for THIS rung. A mismatch is how a rung-4 answer
        // would end up attached to a rung-2 result, which is a live rung.
        if ((reveal as RevealBody).rung !== value['rung']) return null;
      }
      if (value['attempts'] !== undefined && !isAttemptState(value['attempts'])) return null;
      return value as unknown as RungResolvedMessage;
    }

    case 'opp.state':
      if (!int(value['roundIdx']) || !int(value['rung']) || !num(value['total'])) return null;
      if (!str(value['playerId']) || !str(value['handle'])) return null;
      if (!int(value['fieldHighRung'])) return null;
      if (typeof value['banked'] !== 'boolean' || !str(value['phase'])) return null;
      if (value['lngLat'] !== undefined && !isLngLat(value['lngLat'])) return null;
      return value as unknown as OppStateMessage;

    case 'round.end': {
      if (!int(value['roundIdx'])) return null;
      if (!isRecord(value['you']) || !isRecord(value['opp'])) return null;
      if (!isLngLat(value['targetLngLat'])) return null;
      if (!optStr(value['targetLabel'])) return null;
      const fact = value['fact'];
      if (fact !== undefined) {
        if (!isRecord(fact) || !str(fact['cca3']) || !str(fact['name'])) return null;
        if (typeof fact['line'] !== 'string') return null;
      }
      return value as unknown as RoundEndMessage;
    }

    case 'match.sync': {
      if (!str(value['matchId']) || !num(value['bankedTotal'])) return null;
      if (!Array.isArray(value['history'])) return null;
      const round = value['round'];
      if (round !== null) {
        if (!isRecord(round)) return null;
        if (!int(round['index']) || !int(round['rung']) || !num(round['pot'])) return null;
        if (!isMatchPhase(round['phase'])) return null;
        if (typeof round['banked'] !== 'boolean') return null;
        if (round['pinLngLat'] !== null && !isLngLat(round['pinLngLat'])) return null;
        const attempt = round['pinpointAttempt'];
        if (attempt !== undefined && (!int(attempt) || attempt < 0)) return null;
      }
      return value as unknown as MatchSyncMessage;
    }

    case 'match.end':
      if (!str(value['matchId']) || !Array.isArray(value['scores'])) return null;
      return value as unknown as MatchEndMessage;

    case 'error': {
      const code = value['code'];
      if (!str(code) || !(ERROR_CODES as readonly string[]).includes(code)) return null;
      if (!str(value['message']) || typeof value['fatal'] !== 'boolean') return null;
      return value as unknown as ErrorMessage;
    }

    default:
      return null;
  }
}

/** Narrow a validated server message to one variant. */
export function isServerMessageOf<T extends ServerMessageType>(
  msg: ServerMessage,
  type: T,
): msg is Extract<ServerMessage, { t: T }> {
  return msg.t === type;
}

/** Narrow a validated client message to one variant. */
export function isClientMessageOf<T extends ClientMessageType>(
  msg: ClientMessage,
  type: T,
): msg is Extract<ClientMessage, { t: T }> {
  return msg.t === type;
}

export function encodeMessage(msg: ClientMessage | ServerMessage): string {
  return JSON.stringify(msg);
}
